import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

// Data
import { backgroundColor } from '../../../../lib/theme';
import OnlineStatus from '../../../_core/OnlineStatus/online-status.component';
import { ParentHeader, IParentHeaderProps } from '../../../_core/ParentHeader/parent-header.component';
import { treeFromPath } from '../../../../lib/services/auth.service';
import { SessionType } from '../../../../lib/types/session.types';

interface IFullPageLayout {
  children: ReactNode;
  width?: string;
  maxWidth?: string;
  // When provided AND the route is in the parent tree, renders ParentHeader
  // as a full-bleed compact green strip above the inset body; the body itself
  // sits on a plain background (no gradient). Ignored in the staff tree, so
  // staff pages keep the legacy 304px green band untouched.
  parentHeader?: IParentHeaderProps;
}

// Staff / public pages keep the legacy 304px green band.
const StaffBackground = styled.div`
  background: #fafcfe;
  background: linear-gradient(180deg, rgba(189,215,57,1) 304px, ${backgroundColor} 304px, ${backgroundColor} 100%);
`;

// Parent pages move the green into ParentHeader's strip, so the body just
// uses the solid page background.
const ParentBackground = styled.div`
  background: ${backgroundColor};
  min-height: 100vh;
`;

// Owns the horizontal gutter for every page in the app — see "Page content
// always has a horizontal gutter against the viewport" in CLAUDE.md. Desktop
// keeps the legacy 32px margin; mobile (≤959px) drops to a 16px gutter that's
// still visibly off the viewport edge (vs. the prior `mx: 0` flush bug).
const PageBody = styled.div`
  margin: 32px;

  @media (max-width: 959px) {
    margin: 32px 16px;
  }
`;

const FullPageLayout: React.FC<IFullPageLayout> = ({ children, width = 'auto', maxWidth, parentHeader }) => {
  const { pathname } = useLocation();
  const inParentTree = treeFromPath(pathname) === SessionType.PARENT;
  const Background = inParentTree ? ParentBackground : StaffBackground;

  return (
    <Background>
      <OnlineStatus />
      {inParentTree && parentHeader && <ParentHeader {...parentHeader} />}
      <PageBody id="full-page-layout">
        {children}
      </PageBody>
      {/* Inline-styled hard-pixel spacer — immune to margin collapse, MUI
          spacing-class generation, and styled-components HMR. */}
      <div style={{ height: 8 }} aria-hidden />
    </Background>
  );
};

export default FullPageLayout;
